<script setup>
import { ref, computed } from "vue";

import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import DateTimeFormatSwitcher from "@/components/shared/DateTimeFormatSwitcher.vue";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher.vue";
import UserChangePasswordModal from "@/components/shared/profile/UserChangePasswordModal.vue";
import SnsButton from "@/components/ui/SnsButton.vue";
import SnsInput from "@/components/ui/SnsInput.vue";
import SnsModal from "@/components/ui/SnsModal.vue";
import { toaster } from "@/plugins/toaster.js";

defineProps({
  isModalOn: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);
const store = useStore();
const { t } = useI18n();

const languageSwitcherRef = ref(null);
const dateTimeSwitcherRef = ref(null);
const passwordRef = ref(null);

const originalUserName = ref(store.getters.getUser?.user_name || "");
const userNameInput = ref(originalUserName.value);
const email = computed(() => store.getters.getUser?.email || "");

const userNameError = computed(() => {
  const trimmed = userNameInput.value.trim();
  if (trimmed.length > 0 && trimmed.length < 2) {
    return t("feedback.validation.nameMinLength");
  }
  return null;
});

const userNameCanSave = computed(() => {
  const trimmed = userNameInput.value.trim();
  if (trimmed === originalUserName.value) return false;
  if (trimmed.length > 0 && trimmed.length < 2) return false;
  return true;
});

const canSave = computed(() => {
  const languageCanSave = languageSwitcherRef.value?.canSave ?? false;
  const dateTimeCanSave = dateTimeSwitcherRef.value?.canSave ?? false;
  const passwordCanSave = passwordRef.value?.canSave ?? false;

  return userNameCanSave.value || languageCanSave || dateTimeCanSave || passwordCanSave;
});

const combinedPreview = computed(() => {
  const dateExample = dateTimeSwitcherRef.value?.selectedDateFormat?.example || "";
  const timeExample = dateTimeSwitcherRef.value?.selectedTimeFormat?.example || "";
  if (!dateExample && !timeExample) return "";
  return `${dateExample} \u00b7 ${timeExample}`;
});

const handleSave = async () => {
  if (userNameCanSave.value) {
    try {
      await store.dispatch("updateUserName", userNameInput.value.trim());
      originalUserName.value = userNameInput.value.trim();
      toaster.success(t("feedback.success.default"));
    } catch {
      toaster.error(t("feedback.error.default"));
      return;
    }
  }

  if (languageSwitcherRef.value?.canSave) {
    if (dateTimeSwitcherRef.value?.canSave) {
      dateTimeSwitcherRef.value.save();
    }

    languageSwitcherRef.value.save();

    return;
  }

  if (dateTimeSwitcherRef.value?.canSave) {
    dateTimeSwitcherRef.value.save();
  }

  if (passwordRef.value?.canSave) {
    passwordRef.value.save();

    return;
  }

  emit("close");
};

const handleClose = () => {
  userNameInput.value = originalUserName.value;
  emit("close");
};
</script>

<template>
  <div>
    <SnsModal :is-modal-on="isModalOn" modal-id="user-settings-modal" size="max-w-2xl" @close="handleClose">
      <template #modal-header>
        <div class="flex items-center justify-between w-full">
          <span class="font-bold text-lg">{{ $t("common.label.settings") }}</span>
          <button class="btn btn-ghost btn-sm btn-circle" @click="handleClose">
            <i class="bi-x-lg" />
          </button>
        </div>
      </template>

      <template #modal-body>
        <!-- profile -->
        <div class="flex items-center gap-2 text-base font-semibold mt-4">
          <i class="bi-person" />
          {{ $t("common.label.profile") }}
        </div>
        <div class="border-t border-base-content/10 mt-2 mb-4" />

        <div class="flex gap-4">
          <div class="flex-1">
            <SnsInput v-model="userNameInput" :label="$t('common.label.userName')" :placeholder="originalUserName" />
            <small v-if="userNameError" class="text-error text-xs mt-1">
              {{ userNameError }}
            </small>
          </div>
          <div class="flex-1">
            <SnsInput :model-value="email" :label="$t('common.label.email')" disabled />
          </div>
        </div>

        <!-- preferences -->
        <div class="flex items-center gap-2 text-base font-semibold mt-8">
          <i class="bi-sliders" />
          {{ $t("common.label.preferences") }}
        </div>
        <div class="border-t border-base-content/10 mt-2 mb-4" />

        <div class="flex flex-col gap-2 mb-4">
          <label class="text-sm font-medium">{{ $t("common.label.language") }}</label>
          <LanguageSwitcher ref="languageSwitcherRef" />
        </div>

        <DateTimeFormatSwitcher ref="dateTimeSwitcherRef" :show-examples="false" />
        <p v-if="combinedPreview" class="text-xs text-primary mt-2">
          {{ $t("common.label.preview") }}: {{ combinedPreview }}
        </p>

        <!-- security -->
        <div class="flex items-center gap-2 text-base font-semibold mt-8">
          <i class="bi-shield-lock" />
          {{ $t("common.label.security") }}
        </div>
        <div class="border-t border-base-content/10 mt-2 mb-4" />

        <UserChangePasswordModal ref="passwordRef" @close="handleClose" />
      </template>

      <template #modal-footer>
        <div class="flex justify-end gap-2 pt-4">
          <SnsButton custom-class="btn-ghost btn-sm w-fit" @click="handleClose">
            {{ $t("common.action.close") }}
          </SnsButton>
          <SnsButton custom-class="btn-sm w-fit" :disabled="!canSave" @click="handleSave">
            {{ $t("common.action.save") }}
          </SnsButton>
        </div>
      </template>
    </SnsModal>
  </div>
</template>
